import { Component, OnInit, inject, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, DOCUMENT, DecimalPipe } from '@angular/common';
import { FormsModule, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { ContactService, ContactFormData } from '../core/services/contact.service';
import { SeoService } from '../core/services/seo.service';
import { PublicService, PublicSubscriptionPlan } from '../core/services/public.service';
import { NavigationComponent } from '../shared/navigation.component';
import { FooterComponent } from '../shared/footer.component';

@Component({
  selector: 'app-chatbot-whatsapp',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, NavigationComponent, FooterComponent],
  templateUrl: './chatbot-whatsapp.component.html',
  styleUrls: ['./chatbot-whatsapp.component.css']
})
export class ChatbotWhatsappComponent implements OnInit {
  private title = inject(Title);
  private meta = inject(Meta);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private contactService = inject(ContactService);
  private seoService = inject(SeoService);
  private publicService = inject(PublicService);
  private document = inject(DOCUMENT);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  // Form usando ReactiveFormsModule como en landing principal
  contactForm!: FormGroup;
  isSubmitting = false;
  submitMessage = '';
  submitSuccess = false;

  // Pricing plans
  plans: PublicSubscriptionPlan[] = [];
  plansLoading = true;
  plansError = false;

  // Billing period toggle
  billingPeriod: 'monthly' | 'yearly' = 'monthly';
  readonly yearlyDiscountPercent = 20; // 20% descuento en pago anual

  // Mapeo USD a MXN (aproximado)
  readonly pricesMXN: Record<string, number> = {
    'TRIAL': 0,
    'BASIC': 999,
    'PROFESSIONAL': 2999,
    'ENTERPRISE': 9999
  };

  ngOnInit() {
    this.setupSEO();
    this.initializeForm();
    this.loadPlans();

    // Scroll to top
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
  }

  private initializeForm() {
    this.contactForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[\d\s\-\+\(\)]+$/)]],
      message: ['', [Validators.required, Validators.minLength(10)]]
    });
  }

  private setupSEO() {
    this.seoService.updateSEOData({
      title: 'Chatbot Pezeta | Chatbot WhatsApp con IA para Empresas',
      description: 'Automatiza tu atención al cliente con Chatbot Pezeta. Planes desde $999 MXN/mes. IA GPT-4, CRM integrado, soporte mexicano.',
      keywords: 'chatbot whatsapp, chatbot pezeta, chatbot con ia, bot whatsapp automatico, chatbot mexico, whatsapp business api, crm whatsapp',
      ogTitle: 'Chatbot Pezeta | Automatiza Ventas 24/7',
      ogDescription: 'Chatbot WhatsApp con IA para PyMEs mexicanas. CRM integrado, planes desde $999/mes.',
      ogImage: 'https://chatbot.pezetasolutions.com/images/og-image.webp',
      ogUrl: 'https://chatbot.pezetasolutions.com',
      twitterTitle: 'Chatbot Pezeta | Chatbot WhatsApp con IA',
      twitterDescription: 'Automatiza tu atención al cliente 24/7. IA GPT-4, CRM integrado, soporte mexicano.',
      twitterImage: 'https://chatbot.pezetasolutions.com/images/og-image.webp',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Chatbot Pezeta",
        "description": "Chatbot WhatsApp con IA para automatizar ventas y atención al cliente 24/7",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "offers": {
          "@type": "AggregateOffer",
          "lowPrice": "999",
          "highPrice": "9999",
          "priceCurrency": "MXN",
          "offerCount": "4"
        },
        "provider": {
          "@type": "Organization",
          "name": "Pezeta Solutions",
          "url": "https://chatbot.pezetasolutions.com"
        }
      }
    });
  }

  private loadPlans() {
    this.plansLoading = true;
    this.publicService.getSubscriptionPlans().subscribe({
      next: (plans) => {
        // Filtrar Trial - es solo para demos de vendedores, no se muestra públicamente
        this.plans = plans.filter(p => p.code !== 'TRIAL');
        this.plansLoading = false;
      },
      error: () => {
        this.plansError = true;
        this.plansLoading = false;
      }
    });
  }

  getPriceMXN(plan: PublicSubscriptionPlan): number {
    const monthlyPrice = this.pricesMXN[plan.code] ?? plan.defaultMonthlyPrice * 20;

    if (this.billingPeriod === 'yearly') {
      // Precio anual con descuento, mostrado por mes
      const discountMultiplier = 1 - (this.yearlyDiscountPercent / 100);
      return Math.round(monthlyPrice * discountMultiplier);
    }

    return monthlyPrice;
  }

  getOriginalMonthlyPrice(plan: PublicSubscriptionPlan): number {
    return this.pricesMXN[plan.code] ?? plan.defaultMonthlyPrice * 20;
  }

  getYearlyTotal(plan: PublicSubscriptionPlan): number {
    const monthlyWithDiscount = this.getPriceMXN(plan);
    return monthlyWithDiscount * 12;
  }

  toggleBillingPeriod(period: 'monthly' | 'yearly') {
    this.billingPeriod = period;
  }

  onSubmit() {
    if (this.contactForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.submitMessage = '';

      // Preparar datos con campos predefinidos para chatbot WhatsApp
      const formData: ContactFormData = {
        fullName: this.contactForm.get('name')?.value,
        email: this.contactForm.get('email')?.value,
        phoneNumber: this.contactForm.get('phone')?.value,
        serviceType: 'chatbot-whatsapp', // Predefinido para chatbot WhatsApp
        budgetRange: '50k-250k', // Predefinido para chatbot WhatsApp
        projectDeadline: '1-2-meses', // Predefinido para chatbot WhatsApp
        message: `CONSULTA CHATBOT WHATSAPP: ${this.contactForm.get('message')?.value}`
      };

      this.contactService.sendContactForm(formData).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.submitSuccess = true;
          this.submitMessage = '¡Gracias! Hemos recibido tu solicitud. Te contactaremos en las próximas 24 horas para agendar tu consultoría gratuita.';
          this.contactForm.reset();
          
          // Scroll to message only in browser
          if (isPlatformBrowser(this.platformId)) {
            setTimeout(() => {
              const messageElement = this.document.querySelector('.submit-message');
              if (messageElement) {
                messageElement.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          this.submitSuccess = false;
          this.submitMessage = error.message || 'Error al enviar el formulario. Por favor, intenta nuevamente.';
          
          // Scroll to message only in browser
          if (isPlatformBrowser(this.platformId)) {
            setTimeout(() => {
              const messageElement = this.document.querySelector('.submit-message');
              if (messageElement) {
                messageElement.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched() {
    Object.keys(this.contactForm.controls).forEach(key => {
      const control = this.contactForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.contactForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.contactForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) {
        return 'Este campo es obligatorio';
      }
      if (field.errors['email']) {
        return 'Ingresa un email válido';
      }
      if (field.errors['minlength']) {
        return `Mínimo ${field.errors['minlength'].requiredLength} caracteres`;
      }
      if (field.errors['pattern']) {
        return 'Formato de teléfono inválido';
      }
    }
    return '';
  }

  scrollToSection(sectionId: string) {
    if (isPlatformBrowser(this.platformId)) {
      const element = this.document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  navigateToContact() {
    this.router.navigate(['/contacto']);
  }

  openWhatsApp() {
    if (isPlatformBrowser(this.platformId)) {
      window.open(`https://wa.me/message/GTV3AEMP6MFNC1`, '_blank');
    }
  }
}